"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StatementLineKind = exports.PAYMENT_STATUS = exports.ORDER_STATUS = void 0;
exports.paymentRowState = paymentRowState;
exports.isVoidedLineItem = isVoidedLineItem;
exports.orderStatus = orderStatus;
exports.voidBlockedReason = voidBlockedReason;
exports.paymentLabel = paymentLabel;
exports.sourceLabel = sourceLabel;
exports.lineItemDetails = lineItemDetails;
exports.statementLines = statementLines;
var Teller_1 = require("../../../../DriveScout/Billing/Teller");
var time_1 = require("../../helpers/time");
/*
 * View-model for the order screen (Order.vue). Pure functions over the order
 * the API sends, so the statement can be unit-tested and lifted into the Vue 3
 * app unchanged. Money is never summed here: totals and the balance come from
 * the API (order.total, financial_summary) or from Order.vue's existing
 * computeds, and these functions only arrange and label them.
 */
exports.ORDER_STATUS = {
    VOID: "VOID",
    REFUNDED: "REFUNDED",
};
/** Payment::STATUS_* in drivescout-api. A payment without a status predates them and counts as succeeded. */
exports.PAYMENT_STATUS = {
    SUCCEEDED: "succeeded",
    PENDING: "pending",
    ATTEMPTING: "attempting",
    FAILED: "failed",
};
var PROCESSING_FEE_PRODUCT = "Surcharge/Processing Fee";
var MINUTES_PER_HOUR = 60;
var MINUS_SIGN = "−";
var DATE_TIME_FORMAT = "MM/DD/YYYY h:mm a";
var StatementLineKind;
(function (StatementLineKind) {
    StatementLineKind["Item"] = "item";
    StatementLineKind["Subtotal"] = "subtotal";
    StatementLineKind["Coupon"] = "coupon";
    StatementLineKind["Total"] = "total";
    StatementLineKind["Payment"] = "payment";
    StatementLineKind["NoPayments"] = "no-payments";
    StatementLineKind["Adjustment"] = "adjustment";
    StatementLineKind["Balance"] = "balance";
    StatementLineKind["NetReceived"] = "net-received";
})(StatementLineKind || (exports.StatementLineKind = StatementLineKind = {}));
function paymentRowState(payment) {
    if (payment.void === true) {
        return "voided";
    }
    if (payment.status === exports.PAYMENT_STATUS.FAILED) {
        return "failed";
    }
    if (payment.status === exports.PAYMENT_STATUS.PENDING ||
        payment.status === exports.PAYMENT_STATUS.ATTEMPTING) {
        return "pending";
    }
    return "succeeded";
}
/**
 * A voided card payment keeps its Processing Fee line, linked by payment_id,
 * but the API takes its price off order.total (drivescout-vue3 M4 slice 3,
 * owner ruling 2026-09-28).
 */
function isVoidedLineItem(lineItem, payments) {
    if (lineItem.payment_id == null) {
        return false;
    }
    return payments.some(function (payment) { return payment.id === lineItem.payment_id && payment.void === true; });
}
function orderStatus(order, amountOwed) {
    var summary = order.financial_summary;
    if (order.status === exports.ORDER_STATUS.VOID) {
        return { label: "Void", tone: "grey", showsBalance: false };
    }
    if (order.status === exports.ORDER_STATUS.REFUNDED) {
        return { label: "Refunded", tone: "grey", showsBalance: false };
    }
    if (summary && summary.reconciliation_required) {
        return { label: "Needs review", tone: "red", showsBalance: false };
    }
    if (summary && summary.pending_amount > 0) {
        return { label: "Payment pending", tone: "yellow", showsBalance: false };
    }
    if (amountOwed > 0) {
        var anythingPaid = order.payments.some(function (payment) { return paymentRowState(payment) === "succeeded"; });
        return {
            label: anythingPaid ? "Partly paid" : "Balance due",
            tone: "yellow",
            showsBalance: true,
        };
    }
    return { label: "Paid in full", tone: "green", showsBalance: false };
}
/** Why Void order is unavailable, in words; null when it can be used. Mirrors the button's old :disabled test. */
function voidBlockedReason(order) {
    var summary = order.financial_summary;
    if (order.status === exports.ORDER_STATUS.VOID) {
        return "This order is already void.";
    }
    if (order.status === exports.ORDER_STATUS.REFUNDED) {
        return "This order was refunded, so it can't be voided.";
    }
    if (summary && summary.pending_amount > 0) {
        return "You can't void an order while a payment is pending.";
    }
    if (summary && summary.reconciliation_required) {
        return "This order's payments need to be reconciled before it can be voided.";
    }
    return null;
}
function paymentLabel(payment) {
    if (payment.payment_method === "card") {
        return payment.card_last_4 ? "Card ending ".concat(payment.card_last_4) : "Card";
    }
    if (payment.payment_method === "check") {
        return payment.check_number ? "Check #".concat(payment.check_number) : "Check";
    }
    var method = payment.payment_method || "Other";
    return method.charAt(0).toUpperCase() + method.slice(1);
}
function sourceLabel(source) {
    if (!source) {
        return null;
    }
    var words = source.toLowerCase().replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
}
function hoursLabel(type) {
    var hours = type.pivot.hours / MINUTES_PER_HOUR;
    return "".concat(type.name, ", ").concat(hours, " ").concat(hours === 1 ? "hour" : "hours");
}
/** The sub-lines under a line item: what the package contains, or the fee's description. */
function lineItemDetails(lineItem) {
    if (lineItem.product_name === PROCESSING_FEE_PRODUCT) {
        return lineItem.description ? [lineItem.description] : [];
    }
    var details = lineItem.product_details;
    if (details == null) {
        return [];
    }
    if (typeof details === "string") {
        return [details];
    }
    if (details.sessiontypes) {
        return details.sessiontypes.map(hoursLabel);
    }
    if (details.coursetypes) {
        return details.coursetypes.map(hoursLabel);
    }
    return [];
}
function line(fields) {
    return __assign({ details: [], amount: "", tag: "", struck: false, paymentId: null }, fields);
}
function paymentLine(payment) {
    var state = paymentRowState(payment);
    var fields = {
        kind: StatementLineKind.Payment,
        label: paymentLabel(payment),
        details: payment.charged_on
            ? [(0, time_1.formatDateTime)(payment.charged_on, DATE_TIME_FORMAT)]
            : [],
        testid: "order-payment-".concat(payment.id),
    };
    if (state === "voided" || state === "failed") {
        return line(__assign(__assign({}, fields), { amount: (0, Teller_1.inDollars)(payment.amount), tag: state === "voided" ? "Voided" : "Failed", struck: true }));
    }
    if (state === "pending") {
        return line(__assign(__assign({}, fields), { amount: (0, Teller_1.inDollars)(payment.amount), tag: "Pending", paymentId: payment.id }));
    }
    return line(__assign(__assign({}, fields), { amount: MINUS_SIGN + (0, Teller_1.inDollars)(payment.amount), paymentId: payment.id }));
}
/**
 * The order as one statement, top to bottom: what was bought, the coupon,
 * the total, each payment, refunds, and the balance.
 *
 * subtotal is Order.vue's orderTotal (the line items that count);
 * amountOwed is Order.vue's amountOwed (the API's collection balance).
 */
function statementLines(order, subtotal, amountOwed) {
    var lines = [];
    var summary = order.financial_summary;
    order.lineitems.forEach(function (lineItem, index) {
        var voided = isVoidedLineItem(lineItem, order.payments);
        lines.push(line({
            kind: StatementLineKind.Item,
            label: lineItem.product_name,
            details: lineItemDetails(lineItem),
            amount: (0, Teller_1.inDollars)(Number(lineItem.price)),
            tag: voided ? "Voided" : "",
            struck: voided,
            testid: "order-line-item-".concat(index),
        }));
    });
    if (order.coupons.length > 0) {
        var coupon = order.coupons[0];
        lines.push(line({
            kind: StatementLineKind.Subtotal,
            label: "Subtotal",
            amount: (0, Teller_1.inDollars)(subtotal),
            testid: "order-subtotal",
        }));
        lines.push(line({
            kind: StatementLineKind.Coupon,
            label: "Coupon ".concat(coupon.code()),
            details: [coupon.label()],
            testid: "order-coupon",
        }));
    }
    lines.push(line({
        kind: StatementLineKind.Total,
        label: "Order total",
        amount: (0, Teller_1.inDollars)(order.total),
        testid: "order-total",
    }));
    if (order.payments.length === 0) {
        lines.push(line({
            kind: StatementLineKind.NoPayments,
            label: "No payments yet.",
            testid: "order-no-payments",
        }));
    }
    order.payments.forEach(function (payment) { return lines.push(paymentLine(payment)); });
    if (summary && summary.refunded > 0) {
        lines.push(line({
            kind: StatementLineKind.Adjustment,
            label: "Refunded to student",
            amount: "+" + (0, Teller_1.inDollars)(summary.refunded),
            testid: "order-refunded",
        }));
    }
    if (summary && summary.obligation_reduction > 0) {
        lines.push(line({
            kind: StatementLineKind.Adjustment,
            label: "Order reduced by refund",
            amount: MINUS_SIGN + (0, Teller_1.inDollars)(summary.obligation_reduction),
            testid: "order-obligation-reduction",
        }));
    }
    lines.push(line({
        kind: StatementLineKind.Balance,
        label: "Balance due",
        amount: (0, Teller_1.inDollars)(amountOwed),
        testid: "order-balance",
    }));
    if (summary && summary.refunded > 0) {
        lines.push(line({
            kind: StatementLineKind.NetReceived,
            label: "Net received",
            amount: (0, Teller_1.inDollars)(summary.net_received),
            testid: "order-net-received",
        }));
    }
    return lines;
}
